import type { LngLat } from "../types";

/** Demi-largeur au sol d'une voie OSM, par classe du schéma OpenMapTiles, en mètres. */
const HALF_WIDTH_BY_CLASS: Record<string, number> = {
  motorway: 7,
  trunk: 6,
  primary: 5,
  secondary: 4.5,
  tertiary: 4,
  minor: 3.5,
  service: 2.5,
  track: 1.6,
  path: 1.2,
  raceway: 3,
  busway: 3,
  bus_guideway: 3,
  rail: 1.5,
  transit: 1.5,
};
const DEFAULT_HALF_WIDTH = 2;
/** Les touffes ont du volume : on les garde un peu en retrait du bord de la voie. */
const MARGIN_M = 0.3;

export interface RoadClearings {
  lines: { path: LngLat[]; half: number }[];
  areas: LngLat[][];
}

export interface RoadFeature {
  properties?: Record<string, unknown> | null;
  geometry: { type: string; coordinates?: unknown };
}

type Bounds = [number, number, number, number];

function boundsOf(points: LngLat[]): Bounds {
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  for (const [lng, lat] of points) {
    west = Math.min(west, lng);
    east = Math.max(east, lng);
    south = Math.min(south, lat);
    north = Math.max(north, lat);
  }
  return [west, south, east, north];
}

const rounded = (points: LngLat[]): LngLat[] => points.map(([lng, lat]) => [Math.round(lng * 1e7) / 1e7, Math.round(lat * 1e7) / 1e7]);

/**
 * Un tronçon de voie, ou une zone piétonne, prêt pour `roadClearings` : arrondi, avec sa clé et son
 * emprise. Préparé une fois pour toutes les formes, quand chacune le refaisait pour son compte.
 */
export type IndexedRoad = { key: string; bounds: Bounds } & ({ line: { path: LngLat[]; half: number } } | { area: LngLat[] });

/** Les voies chargées, dédoublonnées : une voie coupée par deux tuiles revient deux fois. */
export function indexRoads(features: RoadFeature[]): IndexedRoad[] {
  const pieces = new Map<string, IndexedRoad>();
  for (const feature of features) {
    const half = (HALF_WIDTH_BY_CLASS[String(feature.properties?.class ?? "")] ?? DEFAULT_HALF_WIDTH) + MARGIN_M;
    const { type, coordinates } = feature.geometry;
    const paths = type === "LineString" ? [coordinates as LngLat[]] : type === "MultiLineString" ? (coordinates as LngLat[][]) : [];
    for (const path of paths) {
      if (path.length < 2) continue;
      const line = { path: rounded(path), half };
      const key = JSON.stringify(line);
      if (!pieces.has(key)) pieces.set(key, { key, bounds: boundsOf(path), line });
    }
    const polygons = type === "Polygon" ? [coordinates as LngLat[][]] : type === "MultiPolygon" ? (coordinates as LngLat[][][]) : [];
    for (const [outer] of polygons) {
      if (!outer || outer.length < 3) continue;
      const area = rounded(outer);
      const key = JSON.stringify(area);
      if (!pieces.has(key)) pieces.set(key, { key, bounds: boundsOf(outer), area });
    }
  }
  return [...pieces.values()];
}

/** Les tronçons dont l'emprise croise `bounds`. */
export function roadsNear(roads: IndexedRoad[], bounds: Bounds): IndexedRoad[] {
  const [west, south, east, north] = bounds;
  return roads.filter(({ bounds: [w, s, e, n] }) => w <= east && e >= west && s <= north && n >= south);
}

/** De quoi ne rien semer sur ces tronçons, triés : l'ordre des tuiles varie d'un appel à l'autre. */
export function clearingsOf(roads: IndexedRoad[]): RoadClearings {
  const sorted = [...roads].sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
  return {
    lines: sorted.flatMap((road) => ("line" in road ? [road.line] : [])),
    areas: sorted.flatMap((road) => ("area" in road ? [road.area] : [])),
  };
}

/** Les voies OSM qui passent dans `bounds`, de quoi ne rien semer dessus. */
export function roadClearings(features: RoadFeature[], bounds: Bounds): RoadClearings {
  return clearingsOf(roadsNear(indexRoads(features), bounds));
}
